import type { PreviewField } from '../../extensions';

export const MAX_DEPTH = 3;

export const isPlainObject = (
  value: unknown,
): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
};

export const getNestedValue = (
  obj: Record<string, unknown>,
  path: string,
): unknown => {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (!isPlainObject(current)) return undefined;
    current = current[part];
  }
  return current;
};

export const hasNestedKey = (
  obj: Record<string, unknown>,
  path: string,
): boolean => getNestedValue(obj, path) !== undefined;

export const humanizeKey = (key: string): string => {
  const spaced = key.replace(/[_-]/g, ' ').trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const isRenderableScalar = (
  value: unknown,
): value is string | number | boolean | null | undefined => {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
};

export const isScalarArray = (
  value: unknown,
): value is (string | number | boolean)[] =>
  Array.isArray(value) &&
  value.every(
    v =>
      typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean',
  );

export const pluralize = (count: number, singular: string): string =>
  count === 1 ? `1 ${singular}` : `${count} ${singular}s`;

const MCP_WRAPPER_KEYS = new Set(['body', 'query_params']);

export const resolveInputBody = (
  input: Record<string, unknown>,
): Record<string, unknown> => {
  if (!('body' in input)) return input;

  let parsedBody: Record<string, unknown> = {};

  if (typeof input.body === 'string') {
    try {
      const parsed = JSON.parse(input.body);
      if (isPlainObject(parsed)) parsedBody = parsed;
      else return input;
    } catch {
      return input;
    }
  } else if (isPlainObject(input.body)) {
    parsedBody = input.body;
  } else {
    return input;
  }

  const topLevel: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (!MCP_WRAPPER_KEYS.has(key) && value !== '') {
      topLevel[key] = value;
    }
  }

  return { ...parsedBody, ...topLevel };
};

export const getDefaultPreviewFields = (
  inputObj: Record<string, unknown>,
): PreviewField[] => {
  const nameField = (): PreviewField[] => {
    if ('displayName' in inputObj)
      return [{ key: 'displayName', label: 'Name' }];
    if ('name' in inputObj) return [{ key: 'name', label: 'Name' }];
    return [];
  };

  return [...nameField(), { key: 'description', label: 'Description' }];
};
